import { cn } from "@/lib/utils";

export const APP_PANEL =
  "rounded-2xl border border-border bg-card text-card-foreground p-4 shadow-sm ring-1 ring-black/[0.04] dark:ring-white/[0.06] sm:rounded-3xl sm:p-6";

export const APP_PANEL_COMPACT =
  "rounded-2xl border border-border bg-card text-card-foreground p-4 shadow-sm ring-1 ring-black/[0.03] dark:ring-white/[0.05] sm:rounded-3xl sm:p-5";

export const APP_PRESSABLE_LINK =
  "inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-muted/60 px-4 text-sm font-medium text-foreground transition-[transform,background-color,border-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:border-primary/30 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted";

export const APP_PRESSABLE_PRIMARY =
  "inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground transition-[transform,background-color,opacity] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary/90";

export const APP_PRESSABLE_SECONDARY =
  "inline-flex min-h-11 items-center justify-center rounded-xl border border-border bg-muted/70 px-4 text-sm font-semibold text-foreground transition-[transform,background-color,border-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:border-primary/25 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted";

export const APP_PRESSABLE_EMERALD =
  "inline-flex min-h-11 items-center justify-center rounded-xl bg-success px-4 text-sm font-semibold text-success-foreground transition-[transform,background-color,opacity] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-success/90";

export const APP_KICKER =
  "text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground sm:text-xs";

export const APP_PAGE_TITLE =
  "text-2xl font-semibold tracking-tight text-foreground sm:text-3xl";

export const APP_PAGE_DESC =
  "mt-1.5 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-[15px]";

export const APP_SECTION_LABEL = "text-sm font-semibold text-foreground/85";

export const APP_INPUT =
  "w-full rounded-xl border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-[border-color,box-shadow,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] focus:border-primary focus:ring-2 focus:ring-ring/35";

export const APP_TABLE_WRAP =
  "overflow-x-auto rounded-2xl border border-border bg-surface text-surface-foreground shadow-sm ring-1 ring-black/[0.03] dark:ring-white/[0.05]";

export const APP_METRIC_CARD = APP_PANEL_COMPACT;

export const APP_FEATURE_CARD = APP_PANEL_COMPACT;

export const APP_TILE_LINK =
  "block rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-sm ring-1 ring-black/[0.03] transition-[transform,border-color,background-color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.99] sm:p-5 [@media(hover:hover)_and_(pointer:fine)]:hover:border-primary/25 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted/40 dark:[@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted/20";

export const APP_CALLOUT_SKY =
  "rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card to-transparent p-6 sm:rounded-3xl sm:p-8";

export const APP_STRIP_SKY =
  "rounded-2xl border border-border bg-gradient-to-br from-primary/12 via-card to-muted/30 p-4 sm:rounded-3xl sm:p-5";

export const APP_CALLOUT_AMBER =
  "rounded-2xl border border-warning/30 bg-warning-muted p-4 text-warning-foreground sm:rounded-3xl sm:p-5";

export const APP_LINK_SKY =
  "font-medium text-primary transition-[opacity,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:opacity-80 [@media(hover:hover)_and_(pointer:fine)]:hover:text-primary/80";

export const APP_LINK_ACCENT =
  "font-medium text-accent transition-[opacity,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:opacity-80 [@media(hover:hover)_and_(pointer:fine)]:hover:text-accent/85";

export function appLoadingSpinner(
  variant: "sky" | "emerald" = "sky",
  size: "sm" | "md" = "sm",
) {
  return cn(
    "rounded-full border-2 border-border/60 dash-spin",
    size === "sm" ? "h-9 w-9" : "h-10 w-10",
    variant === "sky" ? "border-t-primary" : "border-t-success",
  );
}

export function appAdminNavLinkClass(active: boolean) {
  return cn(
    "shrink-0 rounded-lg px-2.5 py-2 text-sm transition-[transform,background-color,color] duration-[160ms] ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98] md:w-full md:px-2",
    active
      ? "bg-primary/15 font-medium text-foreground"
      : "text-muted-foreground [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground",
  );
}

export const APP_HEADER_BAR =
  "sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-md";

export const APP_MODAL_BACKDROP =
  "fixed inset-0 z-50 flex items-end justify-center bg-[var(--modal-backdrop)] p-4 backdrop-blur-[2px] sm:items-center";

export const APP_MODAL_PANEL =
  "dash-result-in max-h-[min(90dvh,920px)] w-full max-w-4xl overflow-y-auto shadow-2xl shadow-black/25 dark:shadow-black/50";
